import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  PawPrint, Plus, Camera, Calendar, Weight, Hash, 
  Edit2, Trash2, ChevronLeft, Syringe, Pill, Stethoscope,
  AlertTriangle, FileText, Clock, CheckCircle, X, Search,
  Share2, UserPlus, Shield, Eye, Mail, MapPin, Phone, Building,
  FolderOpen, Upload, File, Image, FileDown, HardDrive, Maximize2, FileImage
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentViewer from './DocumentViewer';
import VaccinationReminders from './VaccinationReminders';
import DicomGallery from './dicom/DicomGallery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Species options with emojis
const SPECIES_OPTIONS = [
  { value: 'chien', label: 'Chien', emoji: '🐕' },
  { value: 'chat', label: 'Chat', emoji: '🐱' },
  { value: 'lapin', label: 'Lapin', emoji: '🐰' },
  { value: 'oiseau', label: 'Oiseau', emoji: '🐦' },
  { value: 'rongeur', label: 'Rongeur', emoji: '🐹' },
  { value: 'reptile', label: 'Reptile', emoji: '🦎' },
  { value: 'cheval', label: 'Cheval', emoji: '🐴' },
  { value: 'nac', label: 'NAC', emoji: '🦔' },
  { value: 'autre', label: 'Autre', emoji: '🐾' }
];

// Medical record type config
const RECORD_TYPES = {
  vaccination: { label: 'Vaccination', icon: Syringe, color: 'bg-blue-500', bgLight: 'bg-blue-50', textColor: 'text-blue-700' },
  traitement: { label: 'Traitement', icon: Pill, color: 'bg-green-500', bgLight: 'bg-green-50', textColor: 'text-green-700' },
  chirurgie: { label: 'Chirurgie', icon: Stethoscope, color: 'bg-red-500', bgLight: 'bg-red-50', textColor: 'text-red-700' },
  allergie: { label: 'Allergie', icon: AlertTriangle, color: 'bg-orange-500', bgLight: 'bg-orange-50', textColor: 'text-orange-700' },
  consultation: { label: 'Consultation', icon: FileText, color: 'bg-gray-500', bgLight: 'bg-gray-50', textColor: 'text-gray-700' }
};

// Document type config
const DOCUMENT_TYPES = {
  ordonnance: { label: 'Ordonnance', icon: FileText, color: 'bg-blue-500', bgLight: 'bg-blue-50', textColor: 'text-blue-700' },
  analyse: { label: 'Analyse', icon: FileText, color: 'bg-purple-500', bgLight: 'bg-purple-50', textColor: 'text-purple-700' },
  imagerie: { label: 'Imagerie', icon: Image, color: 'bg-cyan-500', bgLight: 'bg-cyan-50', textColor: 'text-cyan-700' },
  compte_rendu: { label: 'Compte-rendu', icon: FileText, color: 'bg-green-500', bgLight: 'bg-green-50', textColor: 'text-green-700' },
  certificat: { label: 'Certificat', icon: FileText, color: 'bg-amber-500', bgLight: 'bg-amber-50', textColor: 'text-amber-700' },
  autre: { label: 'Autre', icon: File, color: 'bg-gray-500', bgLight: 'bg-gray-50', textColor: 'text-gray-700' }
};

const MesAnimaux = ({ token, onBack }) => {
  const [animals, setAnimals] = useState([]);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [recordFilter, setRecordFilter] = useState('all');
  const [sharedVets, setSharedVets] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [vetSearchQuery, setVetSearchQuery] = useState('');
  const [vetSearchResults, setVetSearchResults] = useState([]);
  const [isSearchingVets, setIsSearchingVets] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [storageQuota, setStorageQuota] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentFilter, setDocumentFilter] = useState('all');
  const [viewingDocument, setViewingDocument] = useState(null);
  const [vaccinationOverview, setVaccinationOverview] = useState(null);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);

  // Form states
  const [animalForm, setAnimalForm] = useState({
    name: '',
    species: 'chien',
    breed: '',
    birth_date: '',
    sex: 'inconnu',
    weight: '',
    sterilized: false,
    identification_number: '',
    notes: ''
  });

  const [recordForm, setRecordForm] = useState({
    record_type: 'vaccination',
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    vet_name: '',
    next_due_date: ''
  });

  // Fetch animals on mount
  useEffect(() => {
    fetchAnimals();
    fetchVaccinationOverview();
  }, []);

  // Fetch records when animal is selected
  useEffect(() => {
    if (selectedAnimal) {
      fetchMedicalRecords(selectedAnimal.animal_id);
      fetchReminders(selectedAnimal.animal_id);
      fetchSharedVets(selectedAnimal.animal_id);
      fetchDocuments(selectedAnimal.animal_id);
      fetchStorageQuota();
    }
  }, [selectedAnimal]);

  // Search vets with debounce
  useEffect(() => {
    if (vetSearchQuery.length >= 2) {
      const timer = setTimeout(() => {
        searchVeterinarians(vetSearchQuery);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setVetSearchResults([]);
    }
  }, [vetSearchQuery]);

  const fetchAnimals = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/animals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnimals(data);
      }
    } catch (error) {
      console.error('Error fetching animals:', error);
      toast.error('Erreur lors du chargement des animaux');
    }
    setIsLoading(false);
  };

  const fetchVaccinationOverview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vaccinations/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVaccinationOverview(data);
      }
    } catch (error) {
      console.error('Error fetching vaccination overview:', error);
    }
  };

  const getAnimalVaccinationStatus = (animalId) => {
    if (!vaccinationOverview?.animals) return null;
    const animalSummary = vaccinationOverview.animals.find(a => a.animal_id === animalId);
    if (!animalSummary || animalSummary.total_vaccines === 0) return null;
    return animalSummary.overall_status;
  };

  const VaccinationStatusBadge = ({ status, compact = false }) => {
    if (!status) return null;
    
    const config = {
      a_jour: { label: 'À jour', emoji: '🟢', color: 'bg-green-100 text-green-700 border-green-200' },
      bientot_du: { label: 'Rappel proche', emoji: '🟠', color: 'bg-orange-100 text-orange-700 border-orange-200' },
      en_retard: { label: 'En retard', emoji: '🔴', color: 'bg-red-100 text-red-700 border-red-200' }
    };
    
    const cfg = config[status] || config.a_jour;
    
    if (compact) {
      return (
        <span 
          className="absolute -top-1 -right-1 text-lg" 
          title={`Vaccins: ${cfg.label}`}
          data-testid="vaccination-status-badge"
        >
          {cfg.emoji}
        </span>
      );
    }
    
    return (
      <span 
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}
        data-testid="vaccination-status-badge-full"
      >
        {cfg.emoji} {cfg.label}
      </span>
    );
  };

  const fetchMedicalRecords = async (animalId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${animalId}/records`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMedicalRecords(data);
      }
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  const fetchReminders = async (animalId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${animalId}/reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReminders(data);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const fetchSharedVets = async (animalId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${animalId}/shared-vets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSharedVets(data);
      }
    } catch (error) {
      console.error('Error fetching shared vets:', error);
    }
  };

  const fetchAccessLogs = async (animalId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${animalId}/access-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccessLogs(data);
      }
    } catch (error) {
      console.error('Error fetching access logs:', error);
    }
  };

  const fetchDocuments = async (animalId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${animalId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchStorageQuota = async () => {
    try {
      const response = await fetch(`${API_URL}/api/animals/user/storage-quota`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStorageQuota(data);
      }
    } catch (error) {
      console.error('Error fetching storage quota:', error);
    }
  };

  const handleUploadDocument = async (file, documentType, title, description) => {
    if (!file || !selectedAnimal) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Le fichier dépasse la taille maximale de 10 Mo');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Formats acceptés : PDF, JPG, PNG, GIF, WebP');
      return;
    }

    setIsUploadingDocument(true);
    setUploadProgress(0);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 50));
        }
      };
      
      reader.onload = async () => {
        try {
          setUploadProgress(60);
          
          const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/documents/base64`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              file_data: reader.result,
              file_name: file.name,
              document_type: documentType,
              title: title || file.name,
              description: description
            })
          });

          setUploadProgress(90);

          if (response.ok) {
            const newDoc = await response.json();
            setDocuments([newDoc, ...documents]);
            fetchStorageQuota();
            setShowDocumentModal(false);
            toast.success('Document téléversé avec succès');
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Erreur lors du téléversement');
          }
        } catch (error) {
          toast.error('Erreur lors du téléversement');
        } finally {
          setUploadProgress(100);
          setIsUploadingDocument(false);
        }
      };

      reader.onerror = () => {
        toast.error('Erreur de lecture du fichier');
        setIsUploadingDocument(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors du téléversement');
      setIsUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setDocuments(documents.filter(d => d.document_id !== documentId));
        fetchStorageQuota();
        toast.success('Document supprimé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await fetch(`${API_URL}${doc.file_url}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const searchVeterinarians = async (query) => {
    setIsSearchingVets(true);
    try {
      const response = await fetch(`${API_URL}/api/animals/search/veterinarians?query=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVetSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching vets:', error);
    }
    setIsSearchingVets(false);
  };

  const handleShareWithVet = async (vetUserId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/share/${vetUserId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Accès partagé avec le vétérinaire');
        fetchSharedVets(selectedAnimal.animal_id);
        setShowShareModal(false);
        setVetSearchQuery('');
        setVetSearchResults([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du partage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleInviteVet = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/invite`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Accès partagé avec ${data.vet_name}`);
        fetchSharedVets(selectedAnimal.animal_id);
        setShowShareModal(false);
        setVetSearchQuery('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'invitation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleRevokeAccess = async (vetUserId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer l\'accès à ce vétérinaire ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/share/${vetUserId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Accès révoqué');
        fetchSharedVets(selectedAnimal.animal_id);
      } else {
        toast.error('Erreur lors de la révocation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleCreateAnimal = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/animals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...animalForm,
          weight: animalForm.weight ? parseFloat(animalForm.weight) : null
        })
      });

      if (response.ok) {
        const newAnimal = await response.json();
        setAnimals([...animals, newAnimal]);
        setShowAddForm(false);
        resetAnimalForm();
        toast.success(`${newAnimal.name} a été ajouté !`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleUpdateAnimal = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...animalForm,
          weight: animalForm.weight ? parseFloat(animalForm.weight) : null
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setAnimals(animals.map(a => a.animal_id === updated.animal_id ? updated : a));
        setSelectedAnimal(updated);
        toast.success('Profil mis à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteAnimal = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedAnimal.name} et toutes ses données de santé ?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setAnimals(animals.filter(a => a.animal_id !== selectedAnimal.animal_id));
        setSelectedAnimal(null);
        toast.success('Animal supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/photo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ photo: reader.result })
        });

        if (response.ok) {
          const data = await response.json();
          const updated = { ...selectedAnimal, photo_url: data.photo_url };
          setSelectedAnimal(updated);
          setAnimals(animals.map(a => a.animal_id === updated.animal_id ? updated : a));
          toast.success('Photo mise à jour');
        }
      } catch (error) {
        toast.error('Erreur lors de l\'upload de la photo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCreateRecord = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...recordForm,
          animal_id: selectedAnimal.animal_id
        })
      });

      if (response.ok) {
        const newRecord = await response.json();
        setMedicalRecords([newRecord, ...medicalRecords]);
        setShowRecordForm(false);
        resetRecordForm();
        toast.success('Événement ajouté au carnet de santé');
        
        // Refresh reminders in case one was auto-created
        fetchReminders(selectedAnimal.animal_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Supprimer cet événement médical ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/records/${recordId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMedicalRecords(medicalRecords.filter(r => r.record_id !== recordId));
        toast.success('Événement supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      const response = await fetch(`${API_URL}/api/animals/${selectedAnimal.animal_id}/reminders/${reminderId}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchReminders(selectedAnimal.animal_id);
        toast.success('Rappel complété');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetAnimalForm = () => {
    setAnimalForm({
      name: '',
      species: 'chien',
      breed: '',
      birth_date: '',
      sex: 'inconnu',
      weight: '',
      sterilized: false,
      identification_number: '',
      notes: ''
    });
  };

  const resetRecordForm = () => {
    setRecordForm({
      record_type: 'vaccination',
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      vet_name: '',
      next_due_date: ''
    });
  };

  const getSpeciesEmoji = (species) => {
    const found = SPECIES_OPTIONS.find(s => s.value === species);
    return found ? found.emoji : '🐾';
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();
    
    if (years < 1) {
      return `${months + (years * 12)} mois`;
    }
    return `${years} an${years > 1 ? 's' : ''}`;
  };

  const filteredRecords = recordFilter === 'all' 
    ? medicalRecords 
    : medicalRecords.filter(r => r.record_type === recordFilter);

  // Group records by year for timeline
  const groupedRecords = filteredRecords.reduce((acc, record) => {
    const year = new Date(record.date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(record);
    return acc;
  }, {});

  // === RENDER COMPONENTS ===

  // Animal Card Component
  const AnimalCard = ({ animal, onClick }) => (
    <div 
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md hover:border-green-200 transition-all duration-200"
      data-testid={`animal-card-${animal.animal_id}`}
    >
      <div className="flex items-center gap-4">
        <div className="relative">
          {animal.photo_url ? (
            <img 
              src={`${API_URL}${animal.photo_url}`} 
              alt={animal.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-green-100"
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-3xl">
              {getSpeciesEmoji(animal.species)}
            </div>
          )}
          <VaccinationStatusBadge status={getAnimalVaccinationStatus(animal.animal_id)} compact={true} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800 truncate">{animal.name}</h3>
          <p className="text-sm text-gray-500">
            {SPECIES_OPTIONS.find(s => s.value === animal.species)?.label || animal.species}
            {animal.breed && ` • ${animal.breed}`}
          </p>
          {animal.birth_date && (
            <p className="text-xs text-gray-400 mt-1">{calculateAge(animal.birth_date)}</p>
          )}
        </div>
        <ChevronLeft className="w-5 h-5 text-gray-300 rotate-180" />
      </div>
    </div>
  );

  // Add Animal Form
  const AddAnimalForm = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAddForm(false)}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Ajouter un animal</h2>
            <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleCreateAnimal} className="p-6 space-y-4">
          <div>
            <Label htmlFor="name">Nom de l'animal *</Label>
            <Input
              id="name"
              placeholder="Ex: Max"
              value={animalForm.name}
              onChange={(e) => setAnimalForm({ ...animalForm, name: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label>Espèce *</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {SPECIES_OPTIONS.map(species => (
                <button
                  key={species.value}
                  type="button"
                  onClick={() => setAnimalForm({ ...animalForm, species: species.value })}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    animalForm.species === species.value 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl block mb-1">{species.emoji}</span>
                  <span className="text-xs text-gray-600">{species.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="breed">Race</Label>
            <Input
              id="breed"
              placeholder="Ex: Golden Retriever"
              value={animalForm.breed}
              onChange={(e) => setAnimalForm({ ...animalForm, breed: e.target.value })}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="birth_date">Date de naissance</Label>
              <Input
                id="birth_date"
                type="date"
                value={animalForm.birth_date}
                onChange={(e) => setAnimalForm({ ...animalForm, birth_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Sexe</Label>
              <div className="flex gap-2 mt-1">
                {[
                  { value: 'male', label: '♂ Mâle' },
                  { value: 'femelle', label: '♀ Femelle' }
                ].map(sex => (
                  <button
                    key={sex.value}
                    type="button"
                    onClick={() => setAnimalForm({ ...animalForm, sex: sex.value })}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm transition-all ${
                      animalForm.sex === sex.value 
                        ? 'border-green-500 bg-green-50 text-green-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {sex.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="weight">Poids (kg)</Label>
              <Input
                id="weight"
                type="number"
                step="0.1"
                placeholder="Ex: 12.5"
                value={animalForm.weight}
                onChange={(e) => setAnimalForm({ ...animalForm, weight: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="identification">N° Identification</Label>
              <Input
                id="identification"
                placeholder="Puce / Tatouage"
                value={animalForm.identification_number}
                onChange={(e) => setAnimalForm({ ...animalForm, identification_number: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <input
              type="checkbox"
              id="sterilized"
              checked={animalForm.sterilized}
              onChange={(e) => setAnimalForm({ ...animalForm, sterilized: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <Label htmlFor="sterilized" className="cursor-pointer">Stérilisé(e)</Label>
          </div>

          <Button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white" data-testid="submit-animal-btn">
            <PawPrint className="w-4 h-4 mr-2" />
            Ajouter l'animal
          </Button>
        </form>
      </div>
    </div>
  );

  // Medical Record Form
  const AddRecordForm = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowRecordForm(false)}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Ajouter un événement médical</h2>
            <button onClick={() => setShowRecordForm(false)} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleCreateRecord} className="p-6 space-y-4">
          <div>
            <Label>Type d'événement *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {Object.entries(RECORD_TYPES).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setRecordForm({ ...recordForm, record_type: type })}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      recordForm.record_type === type 
                        ? `border-green-500 ${config.bgLight}` 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${config.textColor}`} />
                    <span className="text-xs text-gray-600">{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="title">Titre *</Label>
            <Input
              id="title"
              placeholder={
                recordForm.record_type === 'vaccination' ? 'Ex: Vaccin rage' :
                recordForm.record_type === 'traitement' ? 'Ex: Antiparasitaire' :
                recordForm.record_type === 'allergie' ? 'Ex: Allergie poulet' :
                'Ex: Consultation annuelle'
              }
              value={recordForm.title}
              onChange={(e) => setRecordForm({ ...recordForm, title: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              placeholder="Notes additionnelles..."
              value={recordForm.description}
              onChange={(e) => setRecordForm({ ...recordForm, description: e.target.value })}
              className="mt-1 w-full p-3 border border-gray-200 rounded-lg resize-none h-20 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={recordForm.date}
                onChange={(e) => setRecordForm({ ...recordForm, date: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="vet_name">Vétérinaire</Label>
              <Input
                id="vet_name"
                placeholder="Dr. ..."
                value={recordForm.vet_name}
                onChange={(e) => setRecordForm({ ...recordForm, vet_name: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          {(recordForm.record_type === 'vaccination' || recordForm.record_type === 'traitement') && (
            <div>
              <Label htmlFor="next_due_date">Prochain rappel</Label>
              <Input
                id="next_due_date"
                type="date"
                value={recordForm.next_due_date}
                onChange={(e) => setRecordForm({ ...recordForm, next_due_date: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Un rappel automatique sera créé à cette date</p>
            </div>
          )}

          <Button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white" data-testid="submit-record-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter à la timeline
          </Button>
        </form>
      </div>
    </div>
  );

  // Animal Detail View
  const AnimalDetailView = () => (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => setSelectedAnimal(null)} 
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          data-testid="back-to-list-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">{selectedAnimal.name}</h2>
      </div>

      {/* Animal Profile Card */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Photo */}
          <div className="relative">
            {selectedAnimal.photo_url ? (
              <img 
                src={`${API_URL}${selectedAnimal.photo_url}`} 
                alt={selectedAnimal.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-green-100"
              />
            ) : (
              <div className="w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-6xl">
                {getSpeciesEmoji(selectedAnimal.species)}
              </div>
            )}
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 p-2 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-colors"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={handlePhotoUpload}
            />
          </div>

          {/* Info */}
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-2xl font-bold text-gray-800">{selectedAnimal.name}</h3>
            <p className="text-gray-500">
              {SPECIES_OPTIONS.find(s => s.value === selectedAnimal.species)?.label}
              {selectedAnimal.breed && ` • ${selectedAnimal.breed}`}
            </p>
            
            <div className="flex flex-wrap gap-3 mt-4 justify-center sm:justify-start">
              {/* Vaccination Status Badge */}
              {getAnimalVaccinationStatus(selectedAnimal.animal_id) && (
                <VaccinationStatusBadge status={getAnimalVaccinationStatus(selectedAnimal.animal_id)} compact={false} />
              )}
              {selectedAnimal.birth_date && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                  <Calendar className="w-4 h-4" />
                  {calculateAge(selectedAnimal.birth_date)}
                </span>
              )}
              {selectedAnimal.weight && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                  <Weight className="w-4 h-4" />
                  {selectedAnimal.weight} kg
                </span>
              )}
              {selectedAnimal.identification_number && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  <Hash className="w-4 h-4" />
                  {selectedAnimal.identification_number}
                </span>
              )}
              {selectedAnimal.sterilized && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Stérilisé(e)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 mb-4">
          <TabsTrigger value="profile" data-testid="tab-profile">Profil</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Carnet</TabsTrigger>
          <TabsTrigger value="vaccinations" data-testid="tab-vaccinations" className="flex items-center gap-1">
            <Syringe className="w-3 h-3" />
            Vaccins
          </TabsTrigger>
          <TabsTrigger value="imaging" data-testid="tab-imaging" className="flex items-center gap-1">
            <FileImage className="w-3 h-3" />
            Imagerie
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="flex items-center gap-1">
            <FolderOpen className="w-3 h-3" />
            Docs
          </TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Rappels</TabsTrigger>
          <TabsTrigger value="sharing" data-testid="tab-sharing" className="flex items-center gap-1">
            <Share2 className="w-3 h-3" />
            Partage
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <form onSubmit={handleUpdateAnimal} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-name">Nom</Label>
                  <Input
                    id="edit-name"
                    value={animalForm.name || selectedAnimal.name}
                    onChange={(e) => setAnimalForm({ ...animalForm, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-breed">Race</Label>
                  <Input
                    id="edit-breed"
                    value={animalForm.breed || selectedAnimal.breed || ''}
                    onChange={(e) => setAnimalForm({ ...animalForm, breed: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-birth">Date de naissance</Label>
                  <Input
                    id="edit-birth"
                    type="date"
                    value={animalForm.birth_date || selectedAnimal.birth_date || ''}
                    onChange={(e) => setAnimalForm({ ...animalForm, birth_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-weight">Poids (kg)</Label>
                  <Input
                    id="edit-weight"
                    type="number"
                    step="0.1"
                    value={animalForm.weight || selectedAnimal.weight || ''}
                    onChange={(e) => setAnimalForm({ ...animalForm, weight: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-id">N° Identification</Label>
                  <Input
                    id="edit-id"
                    value={animalForm.identification_number || selectedAnimal.identification_number || ''}
                    onChange={(e) => setAnimalForm({ ...animalForm, identification_number: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
                <Button type="button" variant="outline" onClick={handleDeleteAnimal} className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <div className="space-y-4">
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setRecordFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    recordFilter === 'all' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Tout
                </button>
                {Object.entries(RECORD_TYPES).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => setRecordFilter(type)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      recordFilter === type ? `${config.color} text-white` : `${config.bgLight} ${config.textColor} hover:opacity-80`
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowRecordForm(true)} className="bg-green-500 hover:bg-green-600 text-white" data-testid="add-record-btn">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              {filteredRecords.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">Aucun événement médical enregistré</p>
                  <Button onClick={() => setShowRecordForm(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter le premier événement
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedRecords)
                    .sort(([a], [b]) => parseInt(b) - parseInt(a))
                    .map(([year, records]) => (
                      <div key={year}>
                        <h3 className="text-lg font-semibold text-gray-400 mb-4">{year}</h3>
                        <div className="space-y-3">
                          {records.map(record => {
                            const config = RECORD_TYPES[record.record_type] || RECORD_TYPES.consultation;
                            const Icon = config.icon;
                            return (
                              <div 
                                key={record.record_id}
                                className={`p-4 rounded-xl ${config.bgLight} border border-gray-100 relative`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className={`p-2 ${config.color} rounded-lg text-white`}>
                                    <Icon className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <div>
                                        <h4 className="font-semibold text-gray-800">{record.title}</h4>
                                        <p className="text-sm text-gray-500">
                                          {new Date(record.date).toLocaleDateString('fr-FR', { 
                                            day: 'numeric', 
                                            month: 'long' 
                                          })}
                                          {record.vet_name && ` • ${record.vet_name}`}
                                        </p>
                                      </div>
                                      <button 
                                        onClick={() => handleDeleteRecord(record.record_id)}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                    {record.description && (
                                      <p className="text-sm text-gray-600 mt-2">{record.description}</p>
                                    )}
                                    {record.next_due_date && (
                                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Prochain rappel : {new Date(record.next_due_date).toLocaleDateString('fr-FR')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Vaccinations Tab */}
        <TabsContent value="vaccinations">
          <VaccinationReminders
            token={token}
            animalId={selectedAnimal.animal_id}
            animalName={selectedAnimal.name}
            animalSpecies={selectedAnimal.species}
          />
        </TabsContent>

        {/* Imaging Tab - DICOM */}
        <TabsContent value="imaging">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <DicomGallery 
              animalId={selectedAnimal.animal_id}
              token={token}
              showUploader={true}
            />
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="space-y-4">
            {/* Storage Quota Bar */}
            {storageQuota && (
              <div className="bg-white rounded-2xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-700">Espace de stockage</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {storageQuota.used_mb} Mo / {storageQuota.quota_mb} Mo
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      storageQuota.usage_percent > 90 ? 'bg-red-500' :
                      storageQuota.usage_percent > 70 ? 'bg-amber-500' :
                      'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(storageQuota.usage_percent, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {storageQuota.remaining_mb} Mo restants
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setDocumentFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    documentFilter === 'all' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Tous
                </button>
                {Object.entries(DOCUMENT_TYPES).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => setDocumentFilter(type)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      documentFilter === type ? `${config.color} text-white` : `${config.bgLight} ${config.textColor} hover:opacity-80`
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
              <Button 
                onClick={() => setShowDocumentModal(true)} 
                className="bg-green-500 hover:bg-green-600 text-white" 
                data-testid="add-document-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>

            {/* Documents List */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">Aucun document enregistré</p>
                  <Button onClick={() => setShowDocumentModal(true)} variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter un document
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents
                    .filter(doc => documentFilter === 'all' || doc.document_type === documentFilter)
                    .map(doc => {
                      const config = DOCUMENT_TYPES[doc.document_type] || DOCUMENT_TYPES.autre;
                      const Icon = config.icon;
                      const isImage = doc.mime_type?.startsWith('image/');
                      
                      return (
                        <div 
                          key={doc.document_id}
                          className={`p-4 rounded-xl ${config.bgLight} border border-gray-100`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Thumbnail or Icon */}
                            <div className={`p-3 ${config.color} rounded-lg text-white flex-shrink-0`}>
                              {isImage ? <Image className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="font-semibold text-gray-800">{doc.title}</h4>
                                  <p className="text-sm text-gray-500">
                                    {new Date(doc.created_at).toLocaleDateString('fr-FR', { 
                                      day: 'numeric', 
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                    {' • '}
                                    {formatFileSize(doc.file_size)}
                                  </p>
                                  {doc.description && (
                                    <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                                  )}
                                </div>
                                
                                {/* Actions */}
                                <div className="flex items-center gap-1">
                                  <button 
                                    onClick={() => setViewingDocument(doc)}
                                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                    title="Prévisualiser"
                                  >
                                    <Maximize2 className="w-5 h-5" />
                                  </button>
                                  <button 
                                    onClick={() => handleDownloadDocument(doc)}
                                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <FileDown className="w-5 h-5" />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteDocument(doc.document_id)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                              
                              {/* File name */}
                              <p className="text-xs text-gray-400 mt-2 truncate">
                                {doc.file_name}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            {reminders.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Aucun rappel programmé</p>
                <p className="text-sm text-gray-400 mt-2">Les rappels sont créés automatiquement lors de l'ajout d'événements médicaux</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reminders.map(reminder => {
                  const isOverdue = new Date(reminder.due_date) < new Date() && reminder.status === 'pending';
                  const isCompleted = reminder.status === 'completed';
                  
                  return (
                    <div 
                      key={reminder.reminder_id}
                      className={`p-4 rounded-xl border ${
                        isCompleted ? 'bg-green-50 border-green-200' :
                        isOverdue ? 'bg-red-50 border-red-200' :
                        'bg-amber-50 border-amber-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            isCompleted ? 'bg-green-500' :
                            isOverdue ? 'bg-red-500' :
                            'bg-amber-500'
                          } text-white`}>
                            {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800">{reminder.title}</h4>
                            <p className={`text-sm ${
                              isOverdue ? 'text-red-600' : 'text-gray-500'
                            }`}>
                              {isOverdue ? 'En retard : ' : 'Prévu le '}
                              {new Date(reminder.due_date).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        {!isCompleted && (
                          <Button 
                            size="sm" 
                            onClick={() => handleCompleteReminder(reminder.reminder_id)}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Fait
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Sharing Tab */}
        <TabsContent value="sharing">
          <div className="space-y-6">
            {/* Share Button */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-green-600" />
                    Partager avec un vétérinaire
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Autorisez un vétérinaire à consulter et enrichir le carnet de santé de {selectedAnimal?.name}
                  </p>
                </div>
                <Button 
                  onClick={() => setShowShareModal(true)} 
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="share-animal-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Inviter un vétérinaire
                </Button>
              </div>

              {/* Shared Vets List */}
              {sharedVets.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    Vétérinaires ayant accès ({sharedVets.length})
                  </h4>
                  {sharedVets.map((vet) => (
                    <div 
                      key={vet.user_id} 
                      className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {vet.name?.charAt(0) || 'V'}
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800">{vet.name}</h5>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {vet.clinic_name}
                          </p>
                          <p className="text-xs text-gray-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {vet.city}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Accès lecture
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRevokeAccess(vet.user_id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Shield className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Aucun vétérinaire n'a accès au carnet de {selectedAnimal?.name}</p>
                  <p className="text-sm text-gray-400 mt-1">Invitez un vétérinaire pour partager le suivi médical</p>
                </div>
              )}
            </div>

            {/* Access Logs */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gray-600" />
                  Historique des accès (RGPD)
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => fetchAccessLogs(selectedAnimal?.animal_id)}
                >
                  Actualiser
                </Button>
              </div>

              {accessLogs.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {accessLogs.map((log, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          log.action === 'view' ? 'bg-blue-500' :
                          log.action === 'create' ? 'bg-green-500' :
                          log.action === 'edit' ? 'bg-orange-500' :
                          log.action === 'delete' ? 'bg-red-500' :
                          log.action === 'share' ? 'bg-purple-500' :
                          'bg-gray-500'
                        }`} />
                        <div>
                          <p className="text-sm text-gray-800">
                            <span className="font-medium">{log.user_name}</span>
                            {' - '}
                            {log.action === 'view' ? 'Consultation' :
                             log.action === 'create' ? 'Création' :
                             log.action === 'edit' ? 'Modification' :
                             log.action === 'delete' ? 'Suppression' :
                             log.action === 'share' ? 'Partage' :
                             log.action === 'export' ? 'Export' :
                             log.action}
                          </p>
                          {log.details && <p className="text-xs text-gray-500">{log.details}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(log.timestamp).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <p className="text-sm">Cliquez sur "Actualiser" pour voir l'historique des accès</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  // === MAIN RENDER ===
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <PawPrint className="w-7 h-7 text-green-500" />
            Mes Animaux
          </h1>
        </div>
        {!selectedAnimal && (
          <Button onClick={() => setShowAddForm(true)} className="bg-green-500 hover:bg-green-600 text-white" data-testid="add-animal-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        )}
      </div>

      {/* Content */}
      {selectedAnimal ? (
        <AnimalDetailView />
      ) : isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Chargement...</p>
        </div>
      ) : animals.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <PawPrint className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Aucun animal enregistré</h2>
          <p className="text-gray-500 mb-6">Ajoutez votre premier compagnon pour commencer son carnet de santé digital</p>
          <Button onClick={() => setShowAddForm(true)} className="bg-green-500 hover:bg-green-600 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter mon premier animal
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {animals.map(animal => (
            <AnimalCard 
              key={animal.animal_id} 
              animal={animal} 
              onClick={() => {
                setSelectedAnimal(animal);
                setAnimalForm({
                  name: animal.name,
                  species: animal.species,
                  breed: animal.breed || '',
                  birth_date: animal.birth_date || '',
                  sex: animal.sex || 'inconnu',
                  weight: animal.weight || '',
                  sterilized: animal.sterilized || false,
                  identification_number: animal.identification_number || '',
                  notes: animal.notes || ''
                });
              }}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddForm && <AddAnimalForm />}
      {showRecordForm && <AddRecordForm />}
      
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-green-600" />
                  Inviter un vétérinaire
                </h2>
                <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Recherchez un vétérinaire inscrit sur vetoRDV pour lui partager l'accès au carnet de santé de {selectedAnimal?.name}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Search input */}
              <div>
                <Label htmlFor="vet-search">Rechercher par nom, clinique ou ville</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="vet-search"
                    placeholder="Ex: Dr. Martin, Clinique du Parc, Lyon..."
                    value={vetSearchQuery}
                    onChange={(e) => setVetSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Search results */}
              {isSearchingVets ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">Recherche en cours...</p>
                </div>
              ) : vetSearchResults.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {vetSearchResults.map((vet) => (
                    <div 
                      key={vet.user_id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-semibold">
                          {vet.name?.charAt(0) || 'V'}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">{vet.name}</h4>
                          <p className="text-sm text-gray-500">{vet.clinic_name}</p>
                          <p className="text-xs text-gray-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {vet.city} {vet.postal_code}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleShareWithVet(vet.user_id)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Share2 className="w-4 h-4 mr-1" />
                        Partager
                      </Button>
                    </div>
                  ))}
                </div>
              ) : vetSearchQuery.length >= 2 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Search className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">Aucun vétérinaire trouvé</p>
                  <p className="text-sm text-gray-400 mt-1">Essayez avec un autre nom ou ville</p>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Search className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">Tapez au moins 2 caractères</p>
                  <p className="text-sm text-gray-400 mt-1">pour rechercher un vétérinaire</p>
                </div>
              )}

              {/* Invite by email */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-500 mb-3">
                  Ou invitez directement par email :
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="veterinaire@email.com"
                    id="invite-email"
                    className="flex-1"
                  />
                  <Button
                    onClick={() => {
                      const email = document.getElementById('invite-email').value;
                      if (email) handleInviteVet(email);
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Inviter
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => !isUploadingDocument && setShowDocumentModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-green-600" />
                  Ajouter un document
                </h2>
                <button 
                  onClick={() => !isUploadingDocument && setShowDocumentModal(false)} 
                  className="p-2 hover:bg-gray-100 rounded-full"
                  disabled={isUploadingDocument}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Formats acceptés : PDF, JPG, PNG, GIF, WebP • Max 10 Mo
              </p>
            </div>
            
            <form 
              className="p-6 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const file = documentInputRef.current?.files?.[0];
                if (file) {
                  handleUploadDocument(
                    file,
                    formData.get('document_type'),
                    formData.get('title'),
                    formData.get('description')
                  );
                }
              }}
            >
              {/* File Input */}
              <div>
                <Label htmlFor="document-file">Fichier *</Label>
                <div 
                  className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
                  onClick={() => documentInputRef.current?.click()}
                >
                  <input 
                    ref={documentInputRef}
                    type="file" 
                    id="document-file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,application/pdf,image/*"
                    className="hidden"
                    required
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Update the title field if empty
                        const titleInput = document.getElementById('doc-title');
                        if (titleInput && !titleInput.value) {
                          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                          titleInput.value = nameWithoutExt;
                        }
                      }
                    }}
                  />
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Cliquez pour sélectionner un fichier</p>
                  <p className="text-xs text-gray-400 mt-1">ou glissez-déposez ici</p>
                </div>
              </div>

              {/* Document Type */}
              <div>
                <Label>Type de document *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {Object.entries(DOCUMENT_TYPES).map(([type, config]) => {
                    const Icon = config.icon;
                    return (
                      <label
                        key={type}
                        className="cursor-pointer"
                      >
                        <input 
                          type="radio" 
                          name="document_type" 
                          value={type} 
                          className="peer hidden" 
                          defaultChecked={type === 'autre'}
                        />
                        <div className={`p-3 rounded-xl border-2 text-center transition-all peer-checked:border-green-500 peer-checked:${config.bgLight} border-gray-200 hover:border-gray-300`}>
                          <Icon className={`w-5 h-5 mx-auto mb-1 ${config.textColor}`} />
                          <span className="text-xs text-gray-600">{config.label}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="doc-title">Titre *</Label>
                <Input
                  id="doc-title"
                  name="title"
                  placeholder="Ex: Résultats analyses sang"
                  required
                  className="mt-1"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="doc-description">Description</Label>
                <textarea
                  id="doc-description"
                  name="description"
                  placeholder="Notes optionnelles..."
                  className="mt-1 w-full p-3 border border-gray-200 rounded-lg resize-none h-20 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Upload Progress */}
              {isUploadingDocument && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Téléversement en cours...</span>
                    <span className="text-green-600 font-medium">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-green-500 transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-green-500 hover:bg-green-600 text-white" 
                disabled={isUploadingDocument}
                data-testid="submit-document-btn"
              >
                {isUploadingDocument ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Téléversement...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Téléverser le document
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          document={viewingDocument}
          token={token}
          onClose={() => setViewingDocument(null)}
        />
      )}
    </div>
  );
};

export default MesAnimaux;
